import React, { useState, useEffect, useRef } from 'react'
import CollapseBlockRight from '../../dashboard/collapseblockright/collapseblockright'
import CollapseBlock from '../../dashboard/CollapseBlockLeft/collapseblockleft'
import './waitingRoom.css'
import ludoGame from '../../../assets/images/ludoGame.png'
import Faces from '../../../assets/images/Faces.png'
import DashboardHeader from '../../dashboard/DashboardHeader/DashboardHeader'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import Loader1 from '../../loaders/Loader1'
import { io } from 'socket.io-client'
import { UserContext } from '../../../utils/contexts/UserContext'

// Constants
const GAME_BASE_URL = process.env.REACT_APP_GAME_SERVER_URL;
const SOCKET_RECONNECTION_ATTEMPTS = 10;
const SOCKET_RECONNECTION_DELAY = 1000;
const SOCKET_CONNECTION_TIMEOUT = 40000;
const AUTO_JOIN_DELAY = 10000;

// Global game window reference
window.gameWindow = null;


function WatingRoom() {
  const { lobbyId, lobbyCode,gameName } = useParams();
  const navigate = useNavigate()
  const userContext = React.useContext(UserContext);
  const socketRef = useRef(null);
  const connectionTimeoutRef = useRef(null);

  // Extract search parameters from URL if they exist
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const actualGameId = searchParams.get('actualGameId');
  // const gameName = searchParams.get('gameName');

  const [isLeftBlockOpen, setIsLeftBlockOpen] = useState(true)
  const [isRightBlockOpen, setIsRightBlockOpen] = useState(true)
  const [blocksOpen, setBlocksOpen] = useState('both')
  const [opponentStatus, setOpponentStatus] = useState('waiting') // waiting, not-active, not-available
  const [isSocketConnected, setIsSocketConnected] = useState(false)
  const [connectionError, setConnectionError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [gameDetails, setGameDetails] = useState(
    actualGameId && gameName ? {
      gameId: actualGameId,
      gameName: gameName,
      lobbyCode: lobbyCode
    } : null
  )
  const [countdownSeconds, setCountdownSeconds] = useState(10)
  const [autoJoinStarted, setAutoJoinStarted] = useState(false)
  const autoJoinTimerRef = useRef(null)

  const cleanupSocket = () => {
    if (socketRef.current) {
      socketRef.current.off("opponent-not-active");
      socketRef.current.off("opponent-not-available");
      socketRef.current.off("opponent-joined-lobby");
      socketRef.current.off("start-tournament-fixture");
      socketRef.current.off("connect");
      socketRef.current.off("disconnect");
      socketRef.current.off("connect_error");
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsSocketConnected(false);
    }
  };

  const initializeSocket = () => {
    return new Promise((resolve, reject) => {
      cleanupSocket();

      
      const token = sessionStorage.getItem("token");
      const parsedToken = JSON.parse(token);
      
      const socket = io(GAME_BASE_URL, {
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: SOCKET_RECONNECTION_ATTEMPTS,
        reconnectionDelay: SOCKET_RECONNECTION_DELAY,
        timeout: SOCKET_CONNECTION_TIMEOUT,
        query: {
          userId: userContext?.user?._id,
          token: `Bearer ${parsedToken.accessToken}`
        }
      });

      connectionTimeoutRef.current = setTimeout(() => {
        console.error("Socket connection timeout");
        reject(new Error("Socket connection timeout"));
        cleanupSocket();
      }, SOCKET_CONNECTION_TIMEOUT);

      socket.on("connect", () => {
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        setConnectionError(false);
        setIsSocketConnected(true);
        
        // Join the waiting room
        socket.emit("join-tournament-waiting-room", {
          userId: userContext?.user?._id,
          lobbyCode: lobbyCode
        });
        
        resolve(socket);
      });

      socket.on("disconnect", () => {
        setIsSocketConnected(false);
        setConnectionError(true);
      });

      socket.on("connect_error", (error) => {
        console.error("Socket connection error:", error);
        setIsSocketConnected(false);
        setConnectionError(true);
        reject(error);
      });

      socket.on("opponent-not-active", () => {
        setOpponentStatus('not-active');
      });

      socket.on("opponent-not-available", () => {
        setOpponentStatus('not-available');
      });

      socket.on("opponent-joined-lobby", (creatorId, gameName, lobbyCode) => {
        setOpponentStatus('joined');
        
        // Store game details for later use
        setGameDetails({
          creatorId,
          gameName,
          lobbyCode
        });
      
        // Get game ID from the lobby
        socket.emit("get-game-details", { lobbyCode }, (response) => {
          if (response && response.gameId) {
            // Update game details with the gameId
            setGameDetails(prev => ({
              ...prev,
              gameId: response.gameId
            }));
            
            // Clear auto-join timer
            if (autoJoinTimerRef.current) {
              clearTimeout(autoJoinTimerRef.current);
              autoJoinTimerRef.current = null;
            }
            
            // Format game name properly (capitalize first letter)
            const formattedGameName = gameName ? gameName.charAt(0).toUpperCase() + gameName.slice(1) : "Chess";
            const stakeAmount = response.stakeAmount || 0;
            
            // Create the game URL with all required parameters
            const url = `${GAME_BASE_URL}/game?gameId=${response.gameId}&playerId=${userContext?.user?._id}&opponentId=${creatorId}&gameName=${formattedGameName}&lobbyId=${lobbyId}&lobbyCode=${lobbyCode}&stakeAmount=${stakeAmount}`;
            
            
            // Open game in new window
            window.open(url, '_blank');
          } else {
            console.error("Missing gameId in response:", response);
            // If we couldn't get a gameId, we'll use the lobbyId instead
            setGameDetails(prev => ({
              ...prev,
              gameId: lobbyId
            }));
            
            // Format game name properly (capitalize first letter)
            const formattedGameName = gameName ? gameName.charAt(0).toUpperCase() + gameName.slice(1) : "Chess";
            const stakeAmount = 0;
            
            // Create the game URL with all required parameters even without a proper gameId
            const url = `${GAME_BASE_URL}/game?gameId=${lobbyId}&playerId=${userContext?.user?._id}&opponentId=${creatorId}&gameName=${formattedGameName}&lobbyId=${lobbyId}&lobbyCode=${lobbyCode}&stakeAmount=${stakeAmount}`;
            
            // Open game in new window
            window.open(url, '_blank');
            
          }
        });
      });

      socket.on("gameEnd", (result) => {

        // Create a complete result object  
        const gameEndResult = {
          ...result,
          playerId: userContext?.user?._id, // Add playerId to determine winner/loser
          stakeAmount: parseInt(gameDetails?.stakeAmount) || 0
        };

        // Store result in localStorage for persistence
        localStorage.setItem('gameResult', JSON.stringify(gameEndResult));

        // Notify server that player has received the result
        socket.emit("resultReceived", {
          gameId: gameDetails?.gameId || lobbyId,
          playerId: userContext?.user?._id,
          result: gameEndResult
        });

        // Close game window if it exists
        if (window.gameWindow && !window.gameWindow.closed) {
          window.gameWindow.close();
        }
        // Navigate to game result page
        // navigate('/game-result', { 
        //   state: { 
        //     result: gameEndResult,
        //     stakeAmount: parseInt(gameDetails?.stakeAmount) || 0
        //   }
        // });
        navigate("/");
      });

      socket.on("start-tournament-fixture", (data) => {
        // Format game name properly (capitalize first letter)
        const formattedGameName = data.gameName ? data.gameName.charAt(0).toUpperCase() + data.gameName.slice(1) : "Chess";
        const stakeAmount = data.stakeAmount || 0;
        
        // Store game details for later handling of game end events
        setGameDetails({
          gameId: data.gameId,
          creatorId: data.opponentId,
          gameName: data.gameName,
          lobbyCode: data.lobbyCode,
          lobbyId: data.lobbyId,
          stakeAmount: stakeAmount,
          playerId: data.playerId
        });
        
        // Create the game URL with all required parameters
        const url = `${GAME_BASE_URL}/game?gameId=${data.gameId}&playerId=${data.playerId}&opponentId=${data.opponentId}&gameName=${formattedGameName}&lobbyId=${data.lobbyId}&lobbyCode=${data.lobbyCode}&stakeAmount=${stakeAmount}`;
        
        // Open game in new window
        window.open(url, '_blank');
      });

      socketRef.current = socket;
    });
  };

  const handleConnectionRetry = () => {
    setConnectionError(false);
    setIsLoading(true);
    
    initializeSocket()
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Connection retry failed:", error);
        setConnectionError(true);
        setIsLoading(false);
      });
  };
  
  // Function to join the game directly
  const joinGame = (opponentId = null) => {
    // Clear any existing auto-join timer
    if (autoJoinTimerRef.current) {
      clearTimeout(autoJoinTimerRef.current);
      autoJoinTimerRef.current = null;
    }
    
    // If we have explicit game details from socket, use them
    if (gameDetails) {
      const { creatorId, gameName } = gameDetails;
      
      // Get the gameId from the server response or use a default
      const gameId = gameDetails.gameId || lobbyId; 
      
      
      // Format game name properly (capitalize first letter)
      const formattedGameName = gameName ? gameName.charAt(0).toUpperCase() + gameName.slice(1) : "Chess";
      const stakeAmount = gameDetails?.stakeAmount || 0;
  
      // Create the game URL with all required parameters
      const url = `${GAME_BASE_URL}/game?gameId=${gameId}&playerId=${userContext?.user?._id}&opponentId=${creatorId}&gameName=${formattedGameName}&lobbyId=${lobbyId}&lobbyCode=${lobbyCode}&stakeAmount=${stakeAmount}`;
      
      
      // Open game in new window
      window.open(url, '_blank');
      return;
    }
    
    // If no explicit game details, use URL params and context to directly open game
    // Use the provided opponentId or try to extract it from socket events
    const opponent = opponentId || "system-generated-opponent-id";
    
    // Check if we have full lobby data from URL state
    const searchParams = new URLSearchParams(window.location.search);
    const actualGameId = searchParams.get('actualGameId');
    const formattedGameName = gameName ? gameName.charAt(0).toUpperCase() + gameName.slice(1) : "Chess";
    
    
    // Get stake amount from gameDetails or URL params
    const stakeAmount = gameDetails?.stakeAmount || searchParams.get('stakeAmount') || 0;
    
    // Create the game URL with all required parameters
    const url = `${GAME_BASE_URL}/game?gameId=${actualGameId || lobbyId}&playerId=${userContext?.user?._id}&opponentId=${opponent}&gameName=${formattedGameName}&lobbyId=${lobbyId}&lobbyCode=${lobbyCode}&stakeAmount=${stakeAmount}`;


    // Check if window is already open
    if (!window.gameWindow || window.gameWindow.closed) {
      // Open game in new window and store reference
      window.gameWindow = window.open(url, '_blank');
    } else {
      // Focus existing window
      window.gameWindow.focus();
    }
    
    // Optionally, after opening the game, navigate back to a different page
    // setTimeout(() => navigate('/'), 500);
  };
  
  // Start the auto-join countdown timer
  const startAutoJoinCountdown = () => {
    setAutoJoinStarted(true);
    
    // Set initial countdown
    setCountdownSeconds(10);
    
    // Start the countdown
    const countdownInterval = setInterval(() => {
      setCountdownSeconds(prevSeconds => {
        if (prevSeconds <= 1) {
          // When countdown reaches 0, clear the interval and join the game
          clearInterval(countdownInterval);
          joinGame();
          return 0;
        }
        return prevSeconds - 1;
      });
    }, 1000);
    
    // Set a timeout to automatically join the game after delay
    autoJoinTimerRef.current = setTimeout(() => {
      joinGame();
    }, AUTO_JOIN_DELAY);
    
    return () => {
      clearInterval(countdownInterval);
      if (autoJoinTimerRef.current) {
        clearTimeout(autoJoinTimerRef.current);
      }
    };
  };

  useEffect(() => {
      // Extract additional parameters from URL if present
      const searchParams = new URLSearchParams(window.location.search);
      const actualGameId = searchParams.get('actualGameId');
      const gameName = searchParams.get('gameName');
      
      if (actualGameId && gameName) {
        // Store these in gameDetails for later use
        setGameDetails({
          gameId: actualGameId,
          gameName: gameName,
          lobbyCode: lobbyCode
        });
      }
      
    initializeSocket()
      .then(() => {
        setIsLoading(false);
        
        // After successful connection, start the auto-join countdown
        startAutoJoinCountdown();
      })
      .catch((error) => {
        console.error("Failed to connect socket:", error);
        setConnectionError(true);
        setIsLoading(false);
      });
  
    return () => {
      cleanupSocket();
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      if (autoJoinTimerRef.current) {
        clearTimeout(autoJoinTimerRef.current);
        autoJoinTimerRef.current = null;
      }
    };
  }, [lobbyCode, userContext?.user?._id]);

  useEffect(() => {
    if(isLeftBlockOpen && isRightBlockOpen){
        setBlocksOpen('both')
    }

    if(!isLeftBlockOpen && !isRightBlockOpen){
        setBlocksOpen('none')
    }

    if((!isLeftBlockOpen && isRightBlockOpen) || (isLeftBlockOpen && !isRightBlockOpen)){
        setBlocksOpen('one')
    }
  }, [isLeftBlockOpen, isRightBlockOpen])

  const getStatusMessage = () => {
    // Get the game name from gameDetails or URL params
    const currentGameName = gameDetails ? gameDetails.gameName : (gameName || 'the game');
    
    if (autoJoinStarted) {
      return `Preparing to start ${currentGameName} in ${countdownSeconds} seconds...`;
    }
    
    switch(opponentStatus) {
      case 'not-active':
        return `Opponent is not active. Waiting for them to join ${currentGameName}...`;
      case 'not-available':
        return `Opponent is not available. ${currentGameName} will be cancelled if they don't join soon.`;
      case 'joined':
        return `Opponent has joined! Starting ${currentGameName}...`;
      default:
        return `Waiting for opponent to join ${currentGameName}...`;
    }
  }

  if (connectionError) {
    return (
      <div style={{ minHeight: '100vh', background: '#130828', color: '#FFF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px' }}>
        <div style={{ color: '#FFF', fontSize: 18, marginBottom: 20, textAlign: 'center' }}>
          Lost connection to the game server
        </div>
        <button
          onClick={handleConnectionRetry}
          style={{
            backgroundColor: '#FFCC00',
            padding: '15px',
            borderRadius: 8,
            width: '80%',
            textAlign: 'center',
            marginBottom: 10,
            border: 'none',
            color: '#000000',
            fontSize: 16,
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Retry Connection
        </button>
        <button
          onClick={() => navigate(-1)}
          style={{
            backgroundColor: 'transparent',
            border: '2px solid #FFCC00',
            padding: '15px',
            borderRadius: 8,
            width: '80%',
            textAlign: 'center',
            color: '#FFCC00',
            fontSize: 16,
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Exit Game
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh' }} className='dashboard font-family-poppins'>
      <DashboardHeader />
      <div style={{ textAlign: 'center', margin: '16px 0', color: '#B0FF4A', fontWeight: 700 }}>
        Tournament Waiting Room<br />
        <span style={{ color: '#FFF', fontWeight: 400 }}>
          Lobby Code: <span style={{ fontWeight: 700 }}>{lobbyCode}</span>
        </span>
        <br />
        <span style={{ color: '#AAA', fontSize: 12 }}>
          Lobby ID: {lobbyId}
        </span>
        <div style={{ marginTop: '8px', color: '#FFF' }}>
          {getStatusMessage()}
        </div>
      </div>

      <div className='p-3 d-lg-flex d-md-flex d-block align-items-start justify-content-between'>
          <div className='d-lg-none d-md-none d-flex align-items-center justify-content-between mb-2'>
            <div className='col-lg-2'>
              <CollapseBlock isSmallScreen={true} />
            </div>
            <div className='col-lg-2'>
                <CollapseBlockRight isSmallScreen={true}/>
            </div>              
          </div>

          <div className={`${isLeftBlockOpen ? 'col-lg-2 col-md-2' : 'col-lg-1 col-md-1'} d-lg-flex d-md-flex d-none`}>
            <CollapseBlock setIsLeftBlockOpen={setIsLeftBlockOpen} />
          </div>          

          <div className={`${blocksOpen == 'both' ? 'col-lg-8 col-md-8' : blocksOpen == 'one' ? 'col-lg-9 col-md-9' : blocksOpen == 'none' ? 'col-lg-10 col-md-10' : ''} col-12 px-lg-4 px-md-4 px-0 d-flex flex-column align-items-center`}>
              <div className='w-100'>
                <img className='col-lg-12 col-md-12 col-12' src={ludoGame} alt="Game" />
              </div>
              <div className='w-100 mb-4'>
                <img src={Faces} className='ludowidth mt-5 col-lg-12 col-12 col-md-12' alt=""/>
              </div>
              <div className='w-100 mb-5 d-flex flex-column align-items-center justify-content-center'>
                {isLoading ? (
                  <div style={{ textAlign: 'center' }}>
                    <Loader1 />
                    <div style={{ color: '#FFF', marginTop: 10 }}>
                      {isSocketConnected ? 'Connecting to game server...' : 'Initializing...'}
                    </div>
                  </div>
                ) : (
                  <>
                    <Loader1 />
                    {autoJoinStarted && (
                      <div style={{ color: '#FFF', marginTop: 20, textAlign: 'center' }}>
                        <div style={{ fontSize: 14, marginBottom: 8 }}>
                          Auto-joining game in <span style={{ color: '#FFCC00', fontWeight: 'bold' }}>{countdownSeconds}</span> seconds
                        </div>
                        <button
                          onClick={() => joinGame()}
                          style={{
                            backgroundColor: '#FFCC00',
                            padding: '12px 24px',
                            borderRadius: 8,
                            border: 'none',
                            color: '#130828',
                            fontSize: 16,
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            marginTop: 12
                          }}
                        >
                          Join Now
                        </button>
                        <div style={{ fontSize: 12, marginTop: 16, color: '#AAA' }}>
                          Click to skip the wait and join immediately
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
          </div>

          <div className={`${isRightBlockOpen ? 'col-lg-2 col-md-2' : 'col-lg-1 col-md-1'} d-lg-flex d-md-flex d-none align-items-center justify-content-end`}>
            <div className=''>
                <CollapseBlockRight setIsRightBlockOpen={setIsRightBlockOpen} />
            </div>
          </div>
      </div>
    </div>
  )
}

export default WatingRoom